/**
 * Phasing (docs/bim-plan/09 §7; landed as the pre-work "phasing fields" slice ahead
 * of the roof round — docs/MODELER.md). Every element carries
 * `phaseCreated`/`phaseDemolished`; "existing-to-remain" is DERIVED, never stored —
 * storing it would drift from the two real fields. This file is that one derivation,
 * pure and unit-tested, so every consumer (2D renderers, PDF export, headless
 * queries) reads an identical status for an element in a given view.
 *
 * PHASE CONSOLIDATION round (Matt, 2026-08-12): the Revit two-dropdown model.
 * - "Always existed" is retired: an untagged element is simply CREATED IN THE FIRST
 *   PHASE (Existing Conditions). The two categories meant the same thing on every
 *   plan; keeping both was a distinction without a difference.
 * - Views always resolve to a concrete phase (`effectivePhase` — a view with no
 *   stored phase reads as the LAST phase, Revit's default for new views); the "off"
 *   state is gone.
 * - The old filter+graphics pair presents as ONE display dropdown with four options
 *   (Existing / Demo / New + Existing / Complete — `PHASE_DISPLAY_OPTIONS`), mapped
 *   to the two stored fields by `phaseDisplayOf`/`phaseDisplayPatch` so no stored
 *   data migrates. Graphic treatments per option are still to be fine-tuned.
 */
import type { ElementRecord, PhaseId, PhaseRecord, ViewRecord } from './types'
import { resolveAllElements, type ModelDoc } from './doc'

/** The three graphic buckets a phased element can fall into, plus 'future' (not yet built). */
export type PhaseStatus = 'existing' | 'new' | 'demo'

export const DEFAULT_PHASE_FILTER: NonNullable<ViewRecord['phaseFilter']> = 'asBuilt'

function orderOf(phases: PhaseRecord[], id: PhaseId | null | undefined): number | null {
  if (!id) return null
  const p = phases.find((ph) => ph.id === id)
  return p ? p.order : null
}

function firstOrder(phases: PhaseRecord[]): number {
  let min = Infinity
  for (const p of phases) if (p.order < min) min = p.order
  return min === Infinity ? 0 : min
}

/**
 * The derived phase status of an element AS OF a view's phase. Absent `phaseCreated`
 * reads as CREATED IN THE FIRST PHASE (Existing Conditions) — the retired "always
 * existed" category collapsed into existing (phase consolidation round); a dangling
 * reference (a deleted phase) degrades the same way, never vanishing. Returns `null`
 * when `viewPhase` is absent — phasing has no effect on this view, so callers should
 * render the element exactly as before phasing existed (only reachable when a model
 * has no phases at all, since views resolve to a concrete phase now).
 */
export function phaseStatus(
  phases: PhaseRecord[],
  el: Pick<ElementRecord, 'phaseCreated' | 'phaseDemolished'>,
  viewPhase: PhaseId | null | undefined,
): PhaseStatus | 'future' | null {
  const viewOrder = orderOf(phases, viewPhase)
  if (viewOrder == null) return null
  const createdOrder = orderOf(phases, el.phaseCreated) ?? firstOrder(phases)
  let demoOrder = orderOf(phases, el.phaseDemolished) ?? Infinity
  // IMPOSSIBLE ORDER (2026-10-16, Dolle Residence): demolished in a phase BEFORE the one
  // it was built in can't happen; the demolition is ignored rather than letting new work
  // read as demo forever. Same-phase built+demolished (temporary work) still reads demo.
  if (demoOrder < createdOrder) demoOrder = Infinity
  if (createdOrder > viewOrder) return 'future'
  if (demoOrder <= viewOrder) return 'demo'
  return createdOrder === viewOrder ? 'new' : 'existing'
}

/**
 * HOSTED-PHASE INHERITANCE (2026-08-26, Matt's test pass: "doors still have
 * overlapping demo/existing lines" — a door on a demolished wall carried no phase
 * tags of its own, so it styled 'existing' full-ink right over its host's
 * demo-dashed linework): a HOSTED element (door/window/fixture) lives and dies
 * with its host unless it says otherwise — its effective phase fields fall back
 * to the host's FIELD-WISE, so an untagged door on a demolished wall is
 * demolished with it, while a NEW door punched into an existing wall keeps its
 * own phaseCreated and only inherits the (absent) demolition. Pass the resolver
 * the caller already has (a byId map lookup, or the host record itself). Every
 * status-map builder and phase gate should derive through this, never raw
 * el.phaseCreated/el.phaseDemolished on a hosted element.
 */
export function hostedPhaseFields(
  el: PhaseChild,
  hostOf: (id: string) => PhaseChild | null | undefined,
  /** the model's phases, for the order-aware half of the OUTLIVES-ITS-HOST rule below;
   *  without them only the same-phase case (the common two-phase model) is caught */
  phases?: PhaseRecord[],
): { phaseCreated?: PhaseId; phaseDemolished?: PhaseId } {
  // PHASE PARENT LINKS (2026-10-10, docs/bim-plan/32 §7.2): the parent is `hostId` OR one
  // of the "belongs to" links tools set without hosting: a Casework Studio sink / faucet /
  // appliance / light (`studioBoxId`), a Stair Studio guard (`studioStairId`), an awning over
  // its opening (`openingId`), a cupola on its roof (`roofId`). Walked UP the chain (an
  // awning, its window, that window's wall), field-wise, nearest parent first, so a sink on
  // a box in a demolished kitchen is demolished with it.
  let created = el.phaseCreated
  let demolished = el.phaseDemolished
  let node: PhaseChild = el
  for (let depth = 0; depth < 4 && !(created && demolished); depth++) {
    const pid = phaseParentId(node)
    if (!pid) break
    const parent = hostOf(pid)
    if (!parent || parent === node) break
    created = created ?? parent.phaseCreated
    demolished = demolished ?? parent.phaseDemolished
    node = parent
  }
  // NEW WORK OUTLIVES ITS HOST (2026-10-16, Sara + Rafael on Dolle Residence: New
  // Construction beds/chairs/tables showed in the Demo view): a piece the user built in
  // a phase does not inherit a host demolition in that SAME phase or earlier. Furniture
  // hosted on an existing floor that is demolished in New Construction was placed AFTER
  // the demo, so it is new work, never demolition. Only when the child carries its OWN
  // phaseCreated; an untagged child still lives and dies with its host.
  if (el.phaseCreated && !el.phaseDemolished && demolished) {
    const dOrder = phases ? orderOf(phases, demolished) : null
    const cOrder = phases ? orderOf(phases, el.phaseCreated) : null
    if (demolished === el.phaseCreated || (dOrder != null && cOrder != null && dOrder <= cOrder)) demolished = undefined
  }
  if (created === el.phaseCreated && demolished === el.phaseDemolished) return el
  return { phaseCreated: created, phaseDemolished: demolished }
}

/** the minimal shape `hostedPhaseFields` reads: an element record, raw or resolved */
export interface PhaseChild {
  phaseCreated?: PhaseId
  phaseDemolished?: PhaseId
  hostId?: string
  category?: string
  params?: Record<string, unknown>
}

/** the element this one lives and dies with for phasing (see hostedPhaseFields) */
export function phaseParentId(el: PhaseChild): string | undefined {
  if (el.hostId) return el.hostId
  const p = el.params
  if (!p) return undefined
  const pick = (v: unknown) => (typeof v === 'string' && v ? v : undefined)
  if (el.category === 'object' || el.category === 'fixture') return pick(p.studioBoxId)
  if (el.category === 'railing') return pick(p.studioStairId)
  if (el.category === 'awning') return pick(p.openingId)
  if (el.category === 'cupola') return pick(p.roofId)
  return undefined
}

/** id -> resolved element, the `hostOf` a kernel schedule passes to `inheritedPhaseStatus` */
export function phaseLookup(model: ModelDoc): Map<string, PhaseChild> {
  return new Map(resolveAllElements(model).map((e) => [e.id as string, e as unknown as PhaseChild]))
}

/** `phaseStatus` through the element's phase parents: what schedules and status maps use */
export function inheritedPhaseStatus(
  phases: PhaseRecord[],
  el: PhaseChild,
  viewPhase: PhaseId | null | undefined,
  hostOf: (id: string) => PhaseChild | null | undefined,
): PhaseStatus | 'future' | null {
  return phaseStatus(phases, hostedPhaseFields(el, hostOf, phases), viewPhase)
}

/**
 * Whether `viewPhase` IS the earliest phase in the timeline — the one case where
 * 'new' vs 'existing' has no meaning (there's no "before" to compare against). Every
 * caller that computes `phaseStatus(phases, el, viewPhase)` already has both
 * arguments in hand, so this is cheap to check right alongside it. `viewPhase` absent
 * (phasing has no effect) reads false — `phaseVisible`'s own `viewIsFirstPhase`
 * param already defaults to false, so this only matters when phasing is active.
 */
export function isFirstPhaseView(phases: PhaseRecord[], viewPhase: PhaseId | null | undefined): boolean {
  const viewOrder = orderOf(phases, viewPhase)
  return viewOrder != null && viewOrder === firstOrder(phases)
}

/**
 * Whether a `phaseFilter` preset shows a given status. 'future' only shows (ghosted,
 * by the renderer) under 'overlay' — every other filter hides not-yet-created work.
 * 'demo' and 'existing' both hide brand-new-this-phase work (a demolition plan and an
 * existing-conditions view each show what stood at the start of the phase — they
 * differ in GRAPHICS, still to be fine-tuned, not visibility); 'asBuilt' hides
 * demolished-by-now work.
 *
 * FIRST-PHASE fix (Matt's report: floors/roofs vanished entirely on a plan whose
 * Phase was set to the very first one — Existing Conditions — with the Existing/Demo
 * display option, which is the single most natural way to configure an as-built
 * documentation plan): at the earliest phase, an untagged element reads `phaseStatus`
 * 'new' by the phase-consolidation convention above (`createdOrder === viewOrder`) —
 * correct for GRAPHICS (it's meant to read as plain, untinted ink, same as
 * 'existing'), but this function's 'demo'/'existing' branch was also treating that
 * 'new' as HIDDEN, which is wrong: 'new' only means real just-built work worth hiding
 * from an existing-conditions read when there's an earlier phase it could have
 * predated. At phase zero there is no earlier phase, so 'new' here IS the existing
 * building — `viewIsFirstPhase` (from `isFirstPhaseView`, above) tells this function
 * that and it's treated exactly like 'existing'. Every OTHER combination (a later
 * phase, or 'asBuilt'/'overlay', which never hid 'new' anyway) is byte-identical to
 * before.
 */
export function phaseVisible(
  filter: NonNullable<ViewRecord['phaseFilter']>,
  status: PhaseStatus | 'future',
  viewIsFirstPhase = false,
): boolean {
  if (status === 'future') return filter === 'overlay'
  if (filter === 'overlay') return true
  if (viewIsFirstPhase && status === 'new') return true
  if (filter === 'demo' || filter === 'existing') return status !== 'new'
  return status !== 'demo'   // 'asBuilt'
}

// ---------- the two-dropdown presentation (phase consolidation round) ----------

/** The five options of the single display dropdown (Revit's "phase filter" slot). */
export type PhaseDisplay = 'existing' | 'demo' | 'newExisting' | 'complete' | 'showAll'

export const PHASE_DISPLAY_OPTIONS: { value: PhaseDisplay; label: string }[] = [
  { value: 'existing', label: 'Existing' },
  { value: 'demo', label: 'Demo' },
  { value: 'newExisting', label: 'New + Existing' },
  { value: 'complete', label: 'Complete' },
  // Matt: everything on one plan at once — existing, new, AND demo'd work together
  // (e.g. to compare what's being removed against what's replacing it). Reuses the
  // 'overlay' filter, which already shows every status including 'future' (ghosted)
  // — the one preset with no visibility hide of its own.
  { value: 'showAll', label: 'Show All' },
]

type StoredPhaseTrio = Pick<ViewRecord, 'phase' | 'phaseFilter' | 'phaseGraphics'> | null | undefined

/**
 * The single display option a view's stored filter+graphics pair presents as.
 * A view that never stored anything reads Complete — the exact look "phasing off"
 * used to give it. 'overlay' reads as Show All (added alongside that option) — it
 * was previously undisplayable and folded into the asBuilt/newExisting bucket.
 */
export function phaseDisplayOf(stored: StoredPhaseTrio): PhaseDisplay {
  const f = stored?.phaseFilter
  if (f === 'existing') return 'existing'
  if (f === 'demo') return 'demo'
  if (f === 'overlay') return 'showAll'
  const g = stored?.phaseGraphics ?? (stored?.phase ? 'coded' : 'plain')
  return g === 'plain' ? 'complete' : 'newExisting'
}

/** The stored filter+graphics pair a display choice writes (no stored-data migration). */
export function phaseDisplayPatch(display: PhaseDisplay): {
  phaseFilter: NonNullable<ViewRecord['phaseFilter']>
  phaseGraphics: NonNullable<ViewRecord['phaseGraphics']>
} {
  switch (display) {
    case 'existing': return { phaseFilter: 'existing', phaseGraphics: 'coded' }
    case 'demo': return { phaseFilter: 'demo', phaseGraphics: 'coded' }
    case 'complete': return { phaseFilter: 'asBuilt', phaseGraphics: 'plain' }
    case 'showAll': return { phaseFilter: 'overlay', phaseGraphics: 'coded' }
    default: return { phaseFilter: 'asBuilt', phaseGraphics: 'coded' }
  }
}

export interface EffectivePhase {
  phase: PhaseId | null
  phaseFilter: NonNullable<ViewRecord['phaseFilter']>
  phaseGraphics: NonNullable<ViewRecord['phaseGraphics']>
}

/**
 * Resolve a view's (or the live 3D viewport's) stored phase settings to concrete
 * values — the read-side replacement for the retired "off" state. No stored phase
 * (or a dangling one) resolves to the LAST phase, Revit's default; `phase` is null
 * only when the model has no phases at all. Graphics default follows the stored
 * phase: a view that HAD a phase keeps its coded default, a view that never stored
 * one defaults plain — so every pre-consolidation view renders exactly as it did.
 */
export function effectivePhase(phases: PhaseRecord[], stored: StoredPhaseTrio): EffectivePhase {
  const validStored = stored?.phase && phases.some((p) => p.id === stored.phase) ? stored.phase : null
  let last: PhaseId | null = null
  let lastOrder = -Infinity
  for (const p of phases) if (p.order > lastOrder) { last = p.id; lastOrder = p.order }
  return {
    phase: validStored ?? last,
    phaseFilter: stored?.phaseFilter ?? DEFAULT_PHASE_FILTER,
    phaseGraphics: stored?.phaseGraphics ?? (stored?.phase ? 'coded' : 'plain'),
  }
}
